import { Box, Typography, useTheme } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'

import GalleryUploader from '@/components/sidebar/components/GalleryUploader'
import ImageSkeleton from '@/components/sidebar/components/ImageSkeleton'
import { imageType } from '@/pages/getRecipe/allInOne/utils/constants'
import type { RootState } from '@/store'
import { useGetCaseDetailsQuery } from '@/store/apiService'
import {
  setCanvasImage,
  setDualCanvasImage,
  setNewImages,
} from '@/store/appSlice'
import { getCaseId } from '@/utils'
import {
  DUAL_CANVAS_INCLUDED_PATHS,
  PHOTO_SUPPORTED_PATH,
} from '@/utils/constants'

import GalleryImageWrapper from './components/GalleryImageWrapper'

interface CaseImage {
  id: string
  image: string
  key_raw: string
  calibrated_image?: boolean
  type?: string
  rawWidth?: number
  rawHeight?: number
  keyLatestImage?: string | null
  note?: string | null
  raw_image?: string
  recipe_data?: Record<string, unknown>
}

export interface CaseDetails {
  images?: CaseImage[]
  id: string
  name: string
}

export interface PresignedUrlResponse {
  url: string
  key: string
  id: string
  type: string
  image?: string
  calibrated_image?: string
  latest_image?: string
  raw_image?: string
  rawWidth?: number
  rawHeight?: number
  note?: string | null
  recipe_data?: Record<string, unknown>
}

const Gallery = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'gallery' })
  const dispatch = useDispatch()
  const location = useLocation()
  const theme = useTheme()
  const [pollingInterval, setPollingInterval] = useState(0)

  const newImages = useSelector((state: RootState) => state.app.newImages)
  const canvasImage = useSelector((state: RootState) => state.app.canvasImage)
  const caseId = getCaseId()
  const imageId = useSelector((state: RootState) => state.project.imageId)
  const isLoggingOut = useSelector((state: RootState) => state.app.isLoggingOut)

  const { data: caseDetails, refetch } = useGetCaseDetailsQuery(
    { caseId },
    { skip: !caseId || isLoggingOut, pollingInterval },
  )

  const hiddenPaths = ['/shade-guide-ai']
  const shouldRenderDialog = !hiddenPaths.includes(location.pathname)

  const isDualCanvas = DUAL_CANVAS_INCLUDED_PATHS.includes(location.pathname)

  useEffect(() => {
    if (caseId) {
      refetch()
    }
  }, [caseId, refetch])

  useEffect(() => {
    const images = caseDetails?.images || []

    if (imageId) {
      const matchedImages = Array.isArray(imageId)
        ? images.filter((img) => imageId.includes(img.id))
        : images.filter((img) => img.id === imageId)

      if (matchedImages.length > 0) {
        dispatch(setNewImages(matchedImages))
      }
    }
  }, [caseDetails, imageId])

  useEffect(() => {
    const images = caseDetails?.images || []

    if (!newImages?.length || !images?.length) return

    const updated = newImages.flatMap((img) => {
      if ((img as any)?.presignedData?.key) {
        const matched = images.find(
          (i) => i.key_raw === (img as any).presignedData.key,
        )
        return matched ? [matched] : [img]
      }

      if (img instanceof File) return [img]

      if ((img as unknown as CaseImage)?.id) {
        const match = images.find(
          (i: { id: string }) => i.id === (img as unknown as CaseImage).id,
        )
        return [match || img]
      }

      return [img]
    })

    const uniqueImages = updated.filter((img, index, self) => {
      if (!img?.id) return true
      return index === self.findIndex((t) => t?.id === img.id)
    })

    const hasChanged =
      uniqueImages.length !== newImages?.length ||
      uniqueImages.some((img, i) => img !== newImages[i])

    if (hasChanged) {
      dispatch(setNewImages(uniqueImages))
    }
  }, [caseDetails?.images, newImages, dispatch])

  useEffect(() => {
    const allLoaded = (newImages || []).every((image) => image?.id)
    if (allLoaded) {
      setPollingInterval(0)
    }
  }, [newImages])

  useEffect(() => {
    if (newImages?.length === 0) {
      dispatch(setCanvasImage(null))
      dispatch(setDualCanvasImage(null))
      return
    }

    const validImages = newImages?.filter(
      (img: any) =>
        img?.id &&
        (PHOTO_SUPPORTED_PATH.includes(location.pathname) ||
          img?.type !== imageType.PHOTO),
    )

    if (!validImages?.length || isDualCanvas) return

    const firstImage = validImages[0]

    const isNotCalibrated = !firstImage?.calibrated_image

    const shouldUpdateCanvasImage = !canvasImage

    if (
      shouldUpdateCanvasImage &&
      (firstImage?.type !== imageType?.RAW ||
        !isNotCalibrated ||
        !shouldRenderDialog)
    ) {
      dispatch(setCanvasImage(firstImage))
    }
  }, [newImages, dispatch, location.pathname, canvasImage])

  return (
    <>
      <Box
        display="flex"
        flexGrow={1}
        overflow="hidden"
        flexDirection="column"
        position="relative"
        height="100%"
        sx={{ backgroundColor: theme.palette.background.paper }}
        minHeight="350px"
      >
        <Typography
          fontSize="1.25rem"
          px={2}
          py={1}
          color={theme.palette.text.primary}
        >
          {t('gallery')}
        </Typography>
        <Box
          display="flex"
          flexDirection="column"
          flexGrow={1}
          overflow="auto"
          px={2}
        >
          {newImages?.map((image, index) =>
            !PHOTO_SUPPORTED_PATH.includes(location.pathname) &&
            (image as unknown as CaseImage)?.type === imageType.PHOTO ? null : (
                image as unknown as CaseImage
              )?.id ? (
              <GalleryImageWrapper
                image={image as unknown as CaseImage}
                index={index}
                key={(image as unknown as CaseImage).id}
                setNewImages={(images) => dispatch(setNewImages(images))}
                triggerAutoCalibration={
                  !isDualCanvas &&
                  !canvasImage &&
                  index === 0 &&
                  (image as unknown as CaseImage)?.type === imageType.RAW &&
                  !(image as unknown as CaseImage)?.calibrated_image
                }
              />
            ) : (
              <ImageSkeleton
                file={image}
                key={
                  (image as any)?.presignedData?.key ||
                  (image as any)?.file?.name ||
                  index
                }
                setPollingInterval={setPollingInterval}
              />
            ),
          )}
        </Box>
        <GalleryUploader />
      </Box>
    </>
  )
}

export default Gallery
